import { useState } from 'react';
import {
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  Message,
  Modal,
  Popconfirm,
  Space,
  Tabs,
} from '@arco-design/web-react';
import {
  IconLock,
  IconMinusCircle,
  IconPlusCircle,
  IconUserGroup,
} from '@arco-design/web-react/icon';
import { MoneyInput, SensitiveText, StatusPill } from '@/components/ui';
import { cardsApi } from '@/api';
import type { CardSecret, VccCard } from '@/types/card';
import { CardStatusMeta } from '@/types/card';
import { dollarsToCents, formatMoney } from '@/utils/money';

interface CardDetailDrawerProps {
  card: VccCard | null;
  visible: boolean;
  onClose: () => void;
  onChanged: () => void;
}

export default function CardDetailDrawer({
  card,
  visible,
  onClose,
  onChanged,
}: CardDetailDrawerProps) {
  const [secret, setSecret] = useState<CardSecret | null>(null);
  const [refillForm] = Form.useForm<{ amount: number; remark?: string }>();
  const [withdrawForm] = Form.useForm<{ amount: number; remark?: string }>();
  const [submitting, setSubmitting] = useState(false);

  if (!card) return null;
  const meta = CardStatusMeta[card.status];

  const requireStepUpToken = async (scenario: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      let pwd = '';
      Modal.confirm({
        title: '二次安全验证',
        content: (
          <div>
            <div style={{ marginBottom: 8, color: 'var(--up-color-text-tertiary)' }}>
              场景：{scenario}
            </div>
            <Input.Password
              placeholder="支付密码"
              maxLength={32}
              onChange={(v) => {
                pwd = v;
              }}
            />
          </div>
        ),
        okText: '确认',
        cancelText: '取消',
        onOk: async () => {
          if (!pwd) throw new Error('请输入支付密码');
          // 真实环境需要 stepUp 兑换一次性 token，这里直接透传给后端
          resolve(pwd);
        },
        onCancel: () => reject(new Error('用户取消验证')),
      });
    });
  };

  const handleReveal = async (): Promise<string> => {
    const token = await requireStepUpToken('查看卡号 / CVV');
    const result = await cardsApi.revealSecret(card.id, { stepUpToken: token });
    setSecret(result);
    return `${result.pan} · CVV ${result.cvv}`;
  };

  const handleRefill = async () => {
    try {
      const v = await refillForm.validate();
      setSubmitting(true);
      await cardsApi.refill(card.id, {
        amountCents: dollarsToCents(v.amount),
        remark: v.remark,
      });
      Message.success('充值已提交，等待上游清算');
      refillForm.resetFields();
      onChanged();
    } catch (e) {
      if (e instanceof Error) Message.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    try {
      const v = await withdrawForm.validate();
      setSubmitting(true);
      await cardsApi.withdraw(card.id, {
        amountCents: dollarsToCents(v.amount),
        remark: v.remark,
      });
      Message.success('提现已提交，等待上游清算');
      withdrawForm.resetFields();
      onChanged();
    } catch (e) {
      if (e instanceof Error) Message.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async () => {
    try {
      setSubmitting(true);
      await cardsApi.close(card.id);
      Message.success('销卡指令已下发');
      onChanged();
    } catch (e) {
      if (e instanceof Error) Message.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <span>{card.alias || '虚拟卡详情'}</span>
          <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
        </Space>
      }
      width={520}
      visible={visible}
      onCancel={onClose}
      footer={null}
    >
      <Tabs defaultActiveTab="secret">
        <Tabs.TabPane key="secret" title="卡密安全明细">
          <Descriptions
            column={1}
            colon=" : "
            data={[
              { label: '卡 ID', value: card.id },
              { label: '卡段 BIN', value: card.bin },
              { label: '卡号', value: <SensitiveText masked={card.maskedPan} onReveal={handleReveal} /> },
              {
                label: 'CVV',
                value: secret ? (
                  <SensitiveText masked="***" plain={secret.cvv} />
                ) : (
                  <span style={{ color: 'var(--up-color-text-tertiary)' }}>请先解密卡号</span>
                ),
              },
              {
                label: '有效期',
                value: `${String(card.expireMonth).padStart(2, '0')} / ${card.expireYear}`,
              },
              { label: '可用余额', value: formatMoney(card.availableBalanceCents) },
              { label: '总限额', value: formatMoney(card.totalLimitCents) },
              { label: '可重复充值', value: card.reloadable ? '是' : '否' },
              {
                label: '持卡人模板',
                value: (
                  <Space>
                    <IconUserGroup />
                    {card.billingProfileId}
                  </Space>
                ),
              },
            ]}
          />
        </Tabs.TabPane>

        <Tabs.TabPane key="funds" title="额度二次调度" disabled={card.status !== 'ACTIVE'}>
          <div className="up-section-card" style={{ padding: 16 }}>
            <div style={{ marginBottom: 12, fontWeight: 600 }}>
              <IconPlusCircle style={{ color: 'var(--up-color-success)' }} /> 充值
            </div>
            <Form form={refillForm} layout="vertical">
              <Form.Item label="充值金额（USD）" field="amount" rules={[{ required: true }]}>
                <MoneyInput minDollar={1} />
              </Form.Item>
              <Form.Item label="备注" field="remark">
                <Input placeholder="可选" />
              </Form.Item>
              <Button type="primary" loading={submitting} onClick={handleRefill}>
                确认充值
              </Button>
            </Form>
          </div>

          <div className="up-section-card" style={{ padding: 16, marginTop: 16 }}>
            <div style={{ marginBottom: 12, fontWeight: 600 }}>
              <IconMinusCircle style={{ color: 'var(--up-color-warning)' }} /> 余额转出
            </div>
            <Form form={withdrawForm} layout="vertical">
              <Form.Item label="转出金额（USD）" field="amount" rules={[{ required: true }]}>
                <MoneyInput minDollar={0.01} />
              </Form.Item>
              <Form.Item label="备注" field="remark">
                <Input placeholder="可选" />
              </Form.Item>
              <Button status="warning" loading={submitting} onClick={handleWithdraw}>
                转回母账户
              </Button>
            </Form>
          </div>
        </Tabs.TabPane>

        <Tabs.TabPane key="danger" title="高危状态控制">
          <div className="up-section-card" style={{ padding: 16 }}>
            <div style={{ color: 'var(--up-color-danger)', marginBottom: 12 }}>
              <IconLock /> 销卡操作不可逆，剩余余额将自动退回母账户。
            </div>
            <Popconfirm
              focusLock
              title="确认销卡？"
              content="销卡后该卡片立即失效，剩余美分将清算回母账户"
              onOk={handleClose}
            >
              <Button status="danger" loading={submitting} disabled={card.status !== 'ACTIVE'}>
                立即销卡
              </Button>
            </Popconfirm>
          </div>
        </Tabs.TabPane>
      </Tabs>
    </Drawer>
  );
}
